import { Avatar, Box, Container, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ReactNode } from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import VerifiedIcon from 'assets/verified.svg?react';

type ProfileLayoutProps = {
  documentTitle: string;
  imageUrl?: string | null;
  imageFallbackUrl?: string;
  imageAlt: string;
  title: string;
  subtitle?: ReactNode;
  isVerified?: boolean;
  children?: ReactNode;
  actions?: ReactNode;
};

export const ProfileLayout = (props: ProfileLayoutProps): JSX.Element => {
  const {
    documentTitle,
    imageUrl,
    imageFallbackUrl,
    imageAlt,
    title,
    subtitle,
    isVerified,
    children,
    actions,
  } = props;
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{documentTitle}</title>
      </Helmet>

      <Box sx={{ width: '100%', overflowY: 'auto', py: 3 }}>
        <Container maxWidth="xs" disableGutters sx={{ px: 3 }}>
          <Stack spacing={3}>
            <Avatar
              alt={imageAlt}
              src={imageUrl || imageFallbackUrl}
              sx={(theme) => ({
                width: '100%',
                height: 'auto',
                aspectRatio: 1,
                backgroundColor: theme.palette.grey[300],
                borderRadius: 2,
                alignSelf: 'center',
                boxShadow: `0 8px 24px 0px ${alpha('#000000', 0.15)}`,
              })}
              slotProps={{
                img: {
                  style: { objectFit: imageUrl ? 'cover' : 'none' },
                },
              }}
            />

            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h2" color="text.primary">
                  {title}
                  {isVerified && (
                    <VerifiedIcon
                      width="1em"
                      height="1em"
                      style={{
                        verticalAlign: 'text-top',
                        marginLeft: '10px',
                      }}
                      role="img"
                      aria-label={t('common.verified')}
                    />
                  )}
                </Typography>

                {subtitle && (
                  <Typography
                    color="text.secondary"
                    data-testid="profile-headline"
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>

              {children}
            </Stack>

            {actions}
          </Stack>
        </Container>
      </Box>
    </>
  );
};
